import { BoardRoleEnum } from "enums/BoardRoleEnum";
import { FileActionEnum } from "enums/FileActionEnum";
import { SendTypeEnum } from "enums/MethodSendEmailEnum";
import { RunRoleEnum } from "enums/RunRoleEnum";
import { RunStatusEnum } from "enums/RunStatusEnum";
import { SendToEnum } from "enums/SendToEnum";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useMemo } from "react";

import { EnumLabelsContext, IEnumLabelsContext } from "./EnumLabelsContext";

export const EnumLabelsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();

  const value = useMemo<IEnumLabelsContext>(() => {
    function getBoardRole(roleId: number) {
      if (roleId === BoardRoleEnum.Owner) return i18n.Board_Owners;
      if (roleId === BoardRoleEnum.Member) return i18n.Board_Members;
      return roleId.toString();
    }

    function getRunRole(roleId: number) {
      if (roleId === RunRoleEnum.Owner) return i18n.BoardRole_Owner;
      if (roleId === RunRoleEnum.Member) return i18n.BoardRole_Member;
      if (roleId === RunRoleEnum.AdditionalParticipant) return i18n.RunRole_AdditionalParticipant;
      return roleId.toString();
    }

    function getRunTopicCode(s: string): string {
      switch (s.toLowerCase()) {
        case "agenda":
          return i18n.Topic_Agenda;
        case "decision":
          return i18n.Topic_Decision;
        case "info":
          return i18n.Topic_Info;
        case "protocol":
          return i18n.Topic_Protocol;
        default:
          return "";
      }
    }

    function getBoardTitle(s: string) {
      return i18n.Page_Board_Title.replace("{0}", s.trim());
    }

    function getBoardStatus(state: boolean, archivedDate?: string | null) {
      if (archivedDate) return i18n.BoardStatus_Archived;
      return state ? i18n.BoardStatus_Active : i18n.BoardStatus_Inactive;
    }

    function getRunTitle(s: string) {
      return i18n.Page_Run_Title.replace("{{title}}", s.trim());
    }

    function getFileAction(status: FileActionEnum | undefined) {
      switch (status) {
        case FileActionEnum.Overwrite:
          return i18n.FileAction_Overwrite;
        case FileActionEnum.Delete:
          return i18n.FileAction_Delete;
        case FileActionEnum.Upload:
          return i18n.FileAction_New;
        case FileActionEnum.Exists:
        default:
          return i18n.FileAction_Existing;
      }
    }

    function getRunStatusBadgeTitle(status: RunStatusEnum, archivedDate: string | undefined) {
      switch (status) {
        case RunStatusEnum.Archived:
          if (archivedDate) {
            return i18n.RunStatus_ArchivedAndWillBeDeleted.replace("{0}", archivedDate);
          } else {
            return i18n.RunStatus_Archived;
          }
        default:
          return getRunStatus(status);
      }
    }

    function getRunStatus(status: RunStatusEnum): string {
      switch (status) {
        case RunStatusEnum.Preparing:
          return i18n.RunStatus_Preparing;
        case RunStatusEnum.Published:
          return i18n.RunStatus_Published;
        case RunStatusEnum.Republished:
          return i18n.RunStatus_Republished;
        case RunStatusEnum.ClosedFromPublished:
          return i18n.RunStatus_ClosedFromPublished;
        case RunStatusEnum.ClosedFromRepublished:
          return i18n.RunStatus_ClosedFromRepublished;
        case RunStatusEnum.Archived:
          return i18n.RunStatus_Archived;
        case RunStatusEnum.Revise:
          return i18n.Status_Revise;
        case RunStatusEnum.All:
          return i18n.RunStatus_All;
        default:
          return i18n.RunStatus_Unknown;
      }
    }

    function getRunStatusChangeConfirmText(status: RunStatusEnum): string {
      switch (status) {
        case RunStatusEnum.Revise:
          return i18n.Confirm_RunStatus_Change_To_Revise;
        case RunStatusEnum.ClosedFromPublished:
        case RunStatusEnum.ClosedFromRepublished:
          return i18n.Confirm_RunStatus_Change_To_Close;
        default:
          return i18n.Confirm_RunStatus_Change;
      }
    }

    function getRunStatusChangeConfirmTitle(status: RunStatusEnum): string {
      switch (status) {
        case RunStatusEnum.Revise:
          return i18n.Confirm_RunStatus_Change_To_Revise_Title;
        case RunStatusEnum.ClosedFromPublished:
        case RunStatusEnum.ClosedFromRepublished:
          return i18n.Confirm_RunStatus_Change_To_Close_Title;
        case RunStatusEnum.Published:
        case RunStatusEnum.Republished:
          return i18n.Confirm_RunStatus_Change_To_Publish_Title;
        case RunStatusEnum.Archived:
          return i18n.Confirm_RunStatus_Change_To_Archive_Title;
        default:
          return i18n.Actions_ChangeRunStatus;
      }
    }

    function getSendType(value: SendTypeEnum): string {
      switch (value) {
        case SendTypeEnum.DoNotSend:
          return i18n.SendType_DoNotSend;
        case SendTypeEnum.Download:
          return i18n.SendType_Download;
        case SendTypeEnum.FromEdecide:
          return i18n.SendType_FromEdecide;
        default:
          return "";
      }
    }

    function getSendTo(value: SendToEnum): string {
      switch (value) {
        case SendToEnum.All:
          return i18n.SendTo_All;
        case SendToEnum.BoardOwners:
          return i18n.SendTo_BoardOwners;
        case SendToEnum.Participants:
          return i18n.SendTo_Participants;
        case SendToEnum.ParticipantsWhoNotVoted:
          return i18n.SendTo_ParticipantsWhoNotVoted;
        default:
          return "";
      }
    }

    function getTopicContentTitle(key: string): string {
      switch (key.toLowerCase()) {
        case "agenda":
          return i18n.RunTopicType_Agenda_Content;
        case "decision":
          return i18n.RunTopicType_Decision_Content;
        case "info":
          return i18n.RunTopicType_Info_Content;
        case "protocol":
          return i18n.RunTopicType_Protocol_Content;
        default:
          return "";
      }
    }

    function getTopicType(key: string): string {
      switch (key.toLowerCase()) {
        case "agenda":
          return i18n.Topic_Agenda;
        case "decision":
          return i18n.Topic_Decision;
        case "info":
          return i18n.Topic_Info;
        case "protocol":
          return i18n.Topic_Protocol;
        default:
          return "";
      }
    }

    return {
      getBoardRole,
      getBoardStatus,
      getBoardTitle,
      getRunRole,
      getRunTitle,
      getRunStatus,
      getRunStatusBadgeTitle,
      getRunStatusChangeConfirmText,
      getRunStatusChangeConfirmTitle,
      getRunTopicCode,
      getTopicType,
      getTopicContentTitle,
      getFileAction,
      getSendType,
      getSendTo,
    };
  }, [i18n]);

  return <EnumLabelsContext.Provider value={value}>{children}</EnumLabelsContext.Provider>;
};
